import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Clock, MapPin, Menu } from 'lucide-react';
import { SITE, NAV_LINKS, WHATSAPP_LINK } from '../lib/site';

const TopBar = () => (
  <div className="hidden md:block bg-ink text-paper/80 text-[0.72rem] font-medium tracking-[0.08em]">
    <div className="max-w-[1400px] mx-auto px-6 h-9 flex items-center justify-between">
      <div className="flex items-center gap-6">
        <span className="flex items-center gap-1.5">
          <Clock size={12} className="text-clay-light" />
          {SITE.hours}
        </span>
        <span className="flex items-center gap-1.5">
          <MapPin size={12} className="text-clay-light" />
          Málaga capital · Costa del Sol
        </span>
      </div>
      <div className="flex items-center gap-5">
        <span className="hidden lg:inline text-paper/50">Presupuesto gratis en 24h</span>
        <a href={`tel:${SITE.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
          <Phone size={12} className="text-clay-light" />
          {SITE.phoneDisplay}
        </a>
      </div>
    </div>
  </div>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cerrar menú móvil al navegar
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const goTo = (href: string) => {
    if (href.startsWith('/#')) {
      const [path, hash] = href.split('#');
      if (location.pathname !== path) {
        navigate(path);
        setTimeout(() => {
          document.querySelector(`#${hash}`)?.scrollIntoView({ behavior: 'smooth' });
        }, 120);
      } else {
        document.querySelector(`#${hash}`)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-[90]">
        <TopBar />
        <header
          className={`transition-all duration-300 border-b ${
            isScrolled
              ? 'bg-paper/92 backdrop-blur-md border-line shadow-[0_1px_0_rgba(22,19,15,0.04),0_8px_30px_-16px_rgba(22,19,15,0.25)]'
              : 'bg-transparent border-transparent'
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}
        >
          <div className="max-w-[1400px] mx-auto px-6 h-[72px] flex items-center justify-between gap-6">
            {/* Logo (versión oscura para fondo claro) */}
            <button onClick={() => goTo('/')} className="flex items-center shrink-0" aria-label="HGO Servicios — Inicio">
              <img src="/logo-dark.png" alt="HGO Servicios — Construcción y Reformas en Málaga" className="h-11 w-auto" width={139} height={44} />
            </button>

            {/* Nav desktop */}
            <nav className="hidden lg:flex items-center gap-7" aria-label="Navegación principal">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => goTo(link.href)}
                  className="nav-link"
                  aria-current={location.pathname === link.href.split('#')[0] && link.href !== '/#' ? 'page' : undefined}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA + menú móvil */}
            <div className="flex items-center gap-3">
              <a
                href={WHATSAPP_LINK('Hola, quiero solicitar un presupuesto para mi proyecto.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn hidden sm:inline-flex bg-clay text-white"
              >
                Presupuesto gratis
              </a>
              <a
                href={`tel:${SITE.phone}`}
                className="btn md:hidden lg:hidden bg-ink text-paper"
                aria-label={`Llamar a ${SITE.phoneDisplay}`}
              >
                <Phone size={16} />
              </a>
              <button
                onClick={() => setMenuOpen(true)}
                className="lg:hidden grid place-items-center w-11 h-11 rounded-[3px] border border-line bg-paper/80 backdrop-blur transition-transform duration-150 active:scale-95"
                aria-label="Abrir menú"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Mobile menu — overlay editorial */}
      <div
        className={`fixed inset-0 z-[95] bg-ink text-paper transition-all duration-[400ms] ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ transitionDuration: '400ms', transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}
        aria-hidden={!menuOpen}
      >
        <div className="absolute inset-0 blueprint-grid opacity-40" />
        <div className="relative h-full flex flex-col">
          <div className="flex items-center justify-between px-6 h-[72px]">
            <img src="/logo-light.png" alt="HGO Servicios" className="h-9 w-auto" width={114} height={36} />
            <button
              onClick={() => setMenuOpen(false)}
              className="grid place-items-center w-11 h-11 border border-paper/25 rounded-[3px] transition-transform duration-150 active:scale-95"
              aria-label="Cerrar menú"
            >
              <span className="text-xl leading-none">✕</span>
            </button>
          </div>

          <nav className="flex-1 flex flex-col justify-center px-8 gap-1" aria-label="Menú móvil">
            {NAV_LINKS.map((link, i) => (
              <button
                key={link.href}
                onClick={() => { setMenuOpen(false); goTo(link.href); }}
                className="group flex items-baseline gap-4 py-3 text-left border-b border-paper/10"
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                <span className="text-[0.65rem] font-bold tracking-[0.2em] text-clay">0{i + 1}</span>
                <span className="font-display font-bold text-[clamp(1.75rem,6vw,2.5rem)] tracking-tight group-hover:text-clay transition-colors duration-200">
                  {link.label}
                </span>
              </button>
            ))}
          </nav>

          <div className="px-8 pb-10 space-y-4">
            <a href={WHATSAPP_LINK('Hola, quiero solicitar un presupuesto.')} target="_blank" rel="noopener noreferrer" className="btn w-full bg-clay text-white">
              Solicitar presupuesto gratis
            </a>
            <a href={`tel:${SITE.phone}`} className="btn w-full border border-paper/30 text-paper">
              {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
